/* eslint-disable @typescript-eslint/no-misused-promises */
import {Button, Divider, Icon, Popover, Spinner, Text, TextArea} from "@blueprintjs/core";
import styled from "@emotion/styled";
import {useCallback, useEffect, useRef, useState} from "react";
import {Maybe, OrderEvent, Stop, useAddOrderNoteMutation} from "../../../generated/graphql";
import {StyledButton} from "../StyledButtons";
import {NoteIcon} from "../icons/NoteIcon";
import {AddNoteIcon} from "../icons/AddNoteIcon";
import {COLORS} from "../../../constants/constants";
import NoteItem from "./NoteItem";
import {useAppContext} from "../../../ApplicationContext";
import {useGetJob} from "../../../hooks/useGetJob";
import {useDispatchNotesContext} from "../../../providers/DispatchNotesProvider";

export interface IDispatchNoteProps {
  identifier: string;
  orderId: number;
  orderNumber: string;
  orderEvents: OrderEvent[];
  timeZoneOfJobStop?: boolean;
  firstStopTimezone?: Maybe<string>;
  currentStopTimeZone?: Maybe<string>;
}

const DispatchNotes = ({
  identifier,
  orderId,
  orderNumber,
  orderEvents,
  timeZoneOfJobStop,
  firstStopTimezone,
  currentStopTimeZone
}: IDispatchNoteProps) => {
  const {
    appState: {isTimeFormat24hr}
  } = useAppContext();

  const noteContext = useDispatchNotesContext();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const shouldRestore = noteContext.openIdentifier === identifier && !noteContext.isPending(identifier);
  const wasRestoredRef = useRef(shouldRestore);

  const [isOpen, setIsOpen] = useState(shouldRestore);
  const [noteText, setNoteText] = useState(shouldRestore ? noteContext.getNoteText() : "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<{message: string | null; success: boolean}>({
    message: null,
    success: true
  });
  const [noteTimezone, setNoteTimezone] = useState<Maybe<string>>();
  const [isLoadingTimezone, setIsLoadingTimezone] = useState(false);

  const {getJob} = useGetJob([
    "stops.sequence",
    "stops.address",
    "stops.city",
    "stops.state",
    "stops.zip",
    "stops.timeZone"
  ]);
  const [addOrderNote] = useAddOrderNoteMutation();

  const isHasNotes = orderEvents.length > 0;

  const clearStatus = useCallback(() => {
    setStatus({message: null, success: true});
  }, []);

  const setErrorStatus = useCallback((message: string) => {
    setStatus({message, success: false});
  }, []);

  const handleNoteTimezone = useCallback(async () => {
    if (!isHasNotes) return;
    if (firstStopTimezone) {
      setNoteTimezone(firstStopTimezone);
      return;
    }
    setIsLoadingTimezone(true);
    const {data} = await getJob(orderNumber);
    if (data?.searchJobs?.items?.length > 0) {
      const firstStop = [...(data.searchJobs.items[0].stops as Stop[])].sort((a, b) => a.sequence - b.sequence)[0];
      setNoteTimezone(firstStop.timeZone);
    } else {
      setNoteTimezone(currentStopTimeZone);
    }
    setIsLoadingTimezone(false);
  }, [currentStopTimeZone, firstStopTimezone, getJob, isHasNotes, orderNumber]);

  const handleAddNote = useCallback(() => {
    if (noteText.trim().length === 0) {
      setErrorStatus("Note cannot be empty.");
      return;
    }
    setIsSubmitting(true);

    addOrderNote({variables: {orderId: orderId, note: noteText}})
      .then(({data}) => {
        if (data?.addOrderNote) {
          setNoteText("");
          clearStatus();
          setStatus({message: "Note submitted successfully!", success: true});
        } else {
          setErrorStatus("Failed to submit note. Please try again.");
        }
      })
      .catch(() => {
        setErrorStatus("Failed to submit note. Please try again.");
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }, [addOrderNote, clearStatus, noteText, orderId, setErrorStatus]);

  useEffect(() => {
    if (isOpen && isHasNotes && !noteTimezone) {
      handleNoteTimezone();
    }
  }, [isOpen, handleNoteTimezone, isHasNotes, noteTimezone]);

  // Sync noteText to context so it survives grid refresh
  useEffect(() => {
    if (isOpen) {
      noteContext.setNoteText(noteText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteText]);

  return (
    <StyledPopover
      minimal
      isOpen={isOpen}
      onInteraction={(nextOpenState) => {
        setIsOpen(nextOpenState);
        if (!nextOpenState) {
          noteContext.clear();
        }
      }}
      onClosed={clearStatus}
      content={
        <Container>
          <Top>
            <Title>
              <Text style={{fontSize: "14px", fontWeight: "500"}}>Notes for Job #{orderNumber}</Text>
              <GrayText>Visible only to internal employees</GrayText>
            </Title>

            {isLoadingTimezone ? (
              <Spinner size={20} />
            ) : (
              isHasNotes && (
                <NoteList role="dialog-message">
                  {orderEvents
                    .toSorted((a, b) => b.orderEventId - a.orderEventId)
                    .map((event) => (
                      <NoteItem
                        key={event.orderEventId}
                        note={event}
                        isTimeFormat24hr={isTimeFormat24hr}
                        timeZoneOfJobStop={timeZoneOfJobStop}
                        timezone={noteTimezone}
                      />
                    ))}
                </NoteList>
              )
            )}
          </Top>
          <Divider style={{margin: 0}} hidden={!isHasNotes} />
          <Bottom>
            <Status isShown={!!status?.message}>
              {status?.message && (
                <>
                  <Icon
                    color={!status?.success ? COLORS.TEXT_RED : COLORS.GREEN_SUCCESS}
                    size={12}
                    icon={!status?.success ? "error" : "tick-circle"}
                  />
                  <Text
                    data-testid="status-message"
                    style={{color: !status?.success ? COLORS.TEXT_RED : COLORS.GREEN_SUCCESS}}
                  >
                    {status.message}
                  </Text>
                </>
              )}
            </Status>
            <StyledArea
              inputRef={textareaRef}
              data-testid="note-textarea"
              value={noteText}
              onChange={(e) => {
                setNoteText(e.target.value);
                noteContext.setSelection(e.target.selectionStart ?? 0, e.target.selectionEnd ?? 0);
              }}
              onSelect={(e) => {
                const t = e.target as HTMLTextAreaElement;
                noteContext.setSelection(t.selectionStart ?? 0, t.selectionEnd ?? 0);
              }}
              onFocus={() => {
                clearStatus();
                noteContext.setHadFocus(true);
              }}
              onBlur={() => noteContext.setHadFocus(false)}
              placeholder="Write here..."
              maxLength={255}
              intent={!status?.success ? "danger" : "none"}
            />
            <ActionContainer>
              <GrayText>Max 255 characters ({noteText.length}/255)</GrayText>
              <StyledButton
                style={{fontSize: "12px", borderRadius: "8px", padding: "10px 12px"}}
                intent="primary"
                data-testid="add-note-button"
                onClick={handleAddNote}
                text="Add Note"
                loading={isSubmitting}
                disabled={isSubmitting}
              />
            </ActionContainer>
          </Bottom>
        </Container>
      }
      onOpening={handleNoteTimezone}
      onOpened={() => {
        if (wasRestoredRef.current && noteContext.getHadFocus()) {
          const textarea = textareaRef.current;
          if (textarea) {
            const [start, end] = noteContext.getSelection();
            textarea.focus();
            textarea.setSelectionRange(start, end);
          }
        }
        wasRestoredRef.current = false;
      }}
    >
      <NoteButton
        data-testid="dispatch-notes-button"
        ref={(ref) => {
          if (!ref) return;
          ref!.onclick = (e: MouseEvent) => {
            e.stopPropagation();
            const nextState = !isOpen;
            setIsOpen(nextState);
            if (nextState) {
              noteContext.setOpen(identifier);
            } else {
              noteContext.clear();
            }
          };
        }}
        variant="minimal"
        icon={orderEvents.length > 0 ? <NoteIcon fill="#3B82F6" /> : <AddNoteIcon />}
        title="Notes"
      />
    </StyledPopover>
  );
};

export default DispatchNotes;

const NoteButton = styled(Button)`
  min-height: 16px;
  min-width: 16px;
  padding: 2px;
  border-radius: 50%;
`;

const StyledPopover = styled(Popover)`
  user-select: all;

  * {
    &::-webkit-scrollbar {
      width: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: rgba(107, 114, 128, 0.5);
      border-radius: 3px;
    }
  }
`;

const ColumnFlexDiv = styled.div`
  display: flex;
  flex-direction: column;
`;

const Container = styled(ColumnFlexDiv)`
  font-family: Roboto;
  padding: 12px;
  gap: 8px;
  width: 320px;
  font-size: 12px;
  line-height: 16px;
`;

const Top = styled(ColumnFlexDiv)`
  gap: 12px;
`;

const Title = styled(ColumnFlexDiv)`
  gap: 4px;
`;

const NoteList = styled(ColumnFlexDiv)`
  gap: 16px;
  max-height: 240px;
  border-radius: 4px;
  overflow-y: auto;
  padding-right: 2px;

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(107, 114, 128, 0.5);
    border-radius: 3px;
  }
`;

const Bottom = styled(ColumnFlexDiv)`
  gap: 8px;
`;

const Status = styled.div<{isShown?: boolean}>`
  height: ${(props) => (props.isShown ? "12px" : "0")};
  display: flex;
  align-items: center;
  gap: 4px;
  transition: height 0.2s ease-in-out;
`;

const ActionContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const StyledArea = styled(TextArea)`
  outline: none;
  height: 72px !important;
  resize: none;
  border: 1px solid #e5e7eb;
  box-shadow: none;
  border-radius: 8px;

  &.bp5-intent-danger {
    box-shadow: 0 0 0 0 rgba(205, 66, 70, 0), 0 0 0 0 rgba(205, 66, 70, 0), inset 0 0 0 1px ${COLORS.TEXT_RED},
      inset 0 0 0 1px rgba(17, 20, 24, 0.2), inset 0 1px 1px rgba(17, 20, 24, 0.3);
  }

  &::placeholder {
    color: #9ca3af;
    font-size: 12px;
  }

  &:focus {
    border: 1px solid ${COLORS.BLUE_LIGHT};
    box-shadow: 0 0 0 1px ${COLORS.BLUE_LIGHT};
  }

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(107, 114, 128, 0.5);
    border-radius: 3px;
  }
`;

const GrayText = styled(Text)`
  color: #9ca3af;
`;
